import React, { useEffect, useState } from 'react';
import SearchInput from './search-input/search-input';
import useNextPrevious from './hooks/use-search-next-previous/use-next-previous';
import HighlightedItem from './highlighted-item/highlighted-item';
import { getItemHighlights, ItemsHighlights } from './hooks/use-items-highlights/get-items-highlights';
import useDebouncedFilterItems from './hooks/use-debounced-filter-items/use-debounced-filter-items';
import _isFunction from 'lodash/isFunction';

interface Props {
	items: Item[];
	translations: Translations;
	renderItem?: (item: Item, element: JSX.Element) => JSX.Element;
}

interface Translations {
	searchPlaceholder: string;
}

export interface Item {
	value: string;
	id: string;
}

const defaultRenderItem = (item, element) => {
	return (
		<div key={item.id} className="search-item">
			{element}
		</div>
	);
};

const Search: React.FC<Props> = ({ items, translations, renderItem: renderItemProp = defaultRenderItem }) => {
	const [query, setQuery] = useState('');
	const searchedItems = useDebouncedFilterItems(items, query);
	const itemsHighlights = getItemHighlights(searchedItems, query);
	const { next, previous, activeIndex, activeId } = useNextPrevious(searchedItems);

	useEffect(() => {
		setQuery('');
	}, [items]);

	return (
		<div className="search-component">
			<SearchInput
				onSearch={setQuery}
				search={query}
				next={next}
				previous={previous}
				activeIndex={activeIndex}
				total={searchedItems.length}
				placeholder={translations.searchPlaceholder}
			/>
			<div className="search-items">
				{items.map(item => {
					const element = renderItem(item, itemsHighlights, activeId);
					return _isFunction(renderItemProp) ? renderItemProp(item, element) : element;
				})}
			</div>
		</div>
	);
};

const renderItem = (item: Item, itemsHighlights: ItemsHighlights, activeId: string): JSX.Element => {
	if (item.id in itemsHighlights) {
		const { start, end } = itemsHighlights[item.id];
		return (
			<HighlightedItem start={start} end={end} text={item.value} id={item.id} isActive={activeId === item.id} />
		);
	}
	return <span id={item.id}>{item.value}</span>;
};

export default Search;
